import { useState } from "react";
import { Upload, CheckCircle2, Loader2, FileText, ShieldCheck, TrendingUp, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SiteLayout, PageHero, SectionTag, submitCareer, useSEO } from "./shared";

const EMPTY = { name: "", phone: "", email: "", location: "", position: "", experience: "", qualification: "", expected_salary: "", message: "" };
const MAX_MB = 5;

const WHY_JOIN = [
  { icon: ShieldCheck, title: "Meaningful Work", desc: "Contribute to fire safety and life protection across Tripura." },
  { icon: TrendingUp, title: "Hands-On Learning", desc: "Work across fire alarm, hydrant, sprinkler, pump and AMC projects." },
  { icon: Users2, title: "Team-Based Culture", desc: "Join a professional, technically skilled team." },
];

export default function Careers() {
  useSEO(
    "Careers at SecureFlame Fire Systems | Jobs in Tripura",
    "Apply for technician, engineering and field-service roles at SecureFlame Fire Systems Pvt. Ltd., Tripura's fire protection engineering company."
  );
  const [form, setForm] = useState(EMPTY);
  const [cv, setCv] = useState(null);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFileError(""); setCv(null);
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { setFileError("Please upload your CV as a PDF or Word document."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setFileError(`File too large — please upload a CV under ${MAX_MB}MB.`); return; }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!form.name.trim() || !form.phone.trim() || !form.position.trim()) { setError("Please enter your name, phone and position applied for."); return; }
    if (!cv) { setError("Please attach your CV (PDF or Word, under 5MB)."); return; }
    setSubmitting(true); setError("");
    const res = await submitCareer(form, cv);
    setSubmitting(false);
    if (res.ok) { setDone(true); setForm(EMPTY); setCv(null); } else setError(res.error);
  };

  return (
    <SiteLayout>
      <PageHero eyebrow="Careers" title="Build a Safer Future with SecureFlame" subtitle="Join a professional fire protection engineering team working across Tripura — fire alarm, hydrant, sprinkler, pump, extinguisher and AMC projects." />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-3 gap-6">
        {WHY_JOIN.map((w) => (
          <div key={w.title} className="p-7 rounded-sm border border-border bg-card">
            <w.icon className="text-primary mb-4" size={28} />
            <h3 className="font-heading font-bold text-base text-secondary">{w.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{w.desc}</p>
          </div>
        ))}
      </section>

      <section className="bg-muted">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <SectionTag>Apply Now</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary mb-2">Application Form</h2>
          <p className="text-muted-foreground mb-8">No current openings match your profile? Submit your CV anyway — we keep applications on file for future roles.</p>

          <div className="bg-card border border-border rounded-sm p-6 sm:p-8">
            {done ? (
              <div className="text-center py-14" data-testid="careers-success-message">
                <CheckCircle2 className="text-primary mx-auto mb-4" size={44} />
                <h3 className="font-heading font-bold text-2xl text-secondary">Application received — thank you!</h3>
                <p className="mt-2 text-muted-foreground">Our HR team will review your CV and reach out if there's a suitable role.</p>
                <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-sm" data-testid="careers-apply-another-btn">Submit Another Application</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="careers-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="k-name">Full Name *</Label><Input id="k-name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm" data-testid="careers-input-name" /></div>
                  <div><Label htmlFor="k-phone">Mobile Number *</Label><Input id="k-phone" required type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm" data-testid="careers-input-phone" /></div>
                  <div><Label htmlFor="k-email">Email</Label><Input id="k-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-sm" data-testid="careers-input-email" /></div>
                  <div><Label htmlFor="k-location">Current Location</Label><Input id="k-location" value={form.location} onChange={set("location")} className="mt-1.5 rounded-sm" data-testid="careers-input-location" /></div>
                  <div><Label htmlFor="k-position">Position Applied For *</Label><Input id="k-position" required value={form.position} onChange={set("position")} className="mt-1.5 rounded-sm" data-testid="careers-input-position" /></div>
                  <div><Label htmlFor="k-exp">Years of Experience</Label><Input id="k-exp" value={form.experience} onChange={set("experience")} className="mt-1.5 rounded-sm" data-testid="careers-input-experience" /></div>
                  <div><Label htmlFor="k-qual">Qualification</Label><Input id="k-qual" value={form.qualification} onChange={set("qualification")} className="mt-1.5 rounded-sm" data-testid="careers-input-qualification" /></div>
                  <div><Label htmlFor="k-salary">Expected Salary</Label><Input id="k-salary" value={form.expected_salary} onChange={set("expected_salary")} className="mt-1.5 rounded-sm" data-testid="careers-input-salary" /></div>
                </div>
                <div><Label htmlFor="k-message">Message</Label><Textarea id="k-message" rows={3} value={form.message} onChange={set("message")} className="mt-1.5 rounded-sm" data-testid="careers-input-message" /></div>
                <div>
                  <Label htmlFor="k-cv">Upload CV (PDF or Word, max {MAX_MB}MB) *</Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <label htmlFor="k-cv" className="flex items-center gap-2 cursor-pointer text-sm font-medium border border-dashed border-border rounded-sm px-4 py-3 hover:border-primary/50 transition-colors flex-1">
                      <Upload size={16} className="text-primary" /> {cv ? <span className="flex items-center gap-1.5 truncate"><FileText size={14} /> {cv.name}</span> : "Choose file…"}
                    </label>
                    <input id="k-cv" type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid="careers-input-cv" />
                  </div>
                  {fileError && <p className="text-sm text-primary mt-1.5" data-testid="careers-cv-error">{fileError}</p>}
                </div>
                {error && <p className="text-sm text-primary" data-testid="careers-error-message">{error}</p>}
                <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2 w-full sm:w-auto" data-testid="careers-submit-btn">
                  {submitting ? <><Loader2 size={17} className="animate-spin" /> Submitting…</> : "SUBMIT APPLICATION"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
